const LOCAL_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const LOCALIZED_DATE_PATTERN = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;

export type SupportedDateLocale = 'en-GB' | 'en-US';
export type DateLocaleMode = SupportedDateLocale | 'native';

/** Parse a local `YYYY-MM-DD` string into a Date without UTC timezone shifts. */
export function parseLocalDateString(value: string | undefined | null): Date | undefined {
  if (!value || !LOCAL_DATE_PATTERN.test(value)) {
    return undefined;
  }

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined;
  }

  return date;
}

/** Format a Date as a local `YYYY-MM-DD` string without UTC timezone shifts. */
export function formatLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/** Resolve incoming locale strings to the supported display modes. */
export function normalizeDateLocale(locale: string | undefined | null): DateLocaleMode {
  if (!locale) {
    return 'native';
  }

  const normalized = locale.replace('_', '-').toLowerCase();

  if (normalized.startsWith('en-gb')) {
    return 'en-GB';
  }

  if (normalized.startsWith('en-us')) {
    return 'en-US';
  }

  return 'native';
}

/** Format a canonical `YYYY-MM-DD` value for display in the provided locale mode. */
export function formatDateDisplayString(value: string, locale: string | undefined | null): string {
  const parsed = parseLocalDateString(value);
  if (!parsed) {
    return value;
  }

  const mode = normalizeDateLocale(locale);
  if (mode === 'native') {
    return value;
  }

  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');

  return mode === 'en-GB' ? `${day}/${month}/${year}` : `${month}/${day}/${year}`;
}

/**
 * Format a canonical `YYYY-MM-DD` value as a numeric date in the browser's locale (e.g. `19/10/2026`
 * in en-GB), the way a native date input would show it.
 */
export function formatDateBrowserLocaleString(value: string): string {
  const parsed = parseLocalDateString(value);
  if (!parsed) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(parsed);
}

/**
 * Parse a locale-formatted date string into canonical `YYYY-MM-DD`.
 * Returns an empty string when the value is incomplete or invalid.
 */
export function parseDateDisplayString(value: string, locale: string | undefined | null): string {
  const trimmed = value.trim();
  if (trimmed === '') {
    return '';
  }

  const mode = normalizeDateLocale(locale);
  if (mode === 'native') {
    return LOCAL_DATE_PATTERN.test(trimmed) ? trimmed : '';
  }

  const match = LOCALIZED_DATE_PATTERN.exec(trimmed);
  if (!match) {
    return '';
  }

  const [, firstPartRaw, secondPartRaw, yearRaw] = match;
  const firstPart = Number(firstPartRaw);
  const secondPart = Number(secondPartRaw);
  const year = Number(yearRaw);

  const month = mode === 'en-GB' ? secondPart : firstPart;
  const day = mode === 'en-GB' ? firstPart : secondPart;

  const parsed = new Date(year, month - 1, day);
  if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
    return '';
  }

  return formatLocalDateString(parsed);
}
